import React, { useState, useEffect } from 'react'
import shortid from 'shortid'
import { useDispatch } from 'react-redux'

import useOnboarding from '../hooks/useOnboarding'
import { logActionAsync } from '../store/actions'
import onboardingSectionsData from '../static-copy/onboarding-section-data.json'
import ToggleBox from './toggle-box'
import styles from '../component-styles/campaign-codes-generator-help.module.scss'
import RequestAnExpert from './request-an-expert'
import Button from './button'
import { saveUserData, getUserData } from '../local-client/local-client'
import { adobeContent, googleContent } from '../static-copy/track-learn'

interface TrackHelpProps {
  adobe?: boolean
}

export default function TrackHelpContent({
  adobe = false,
}: TrackHelpProps): React.ReactElement {
  const [raeActive, setRaeActive] = useState(false)
  const saveState = getUserData()
  const emptyFields = Array(8).fill(false, 0)
  const initialState =
    saveState && saveState.toggleBoxState
      ? saveState.toggleBoxState
      : emptyFields
  const [toggleBoxState, setToggleBoxState] = useState(initialState)
  const [openBoxState, setOpenBoxState] = useState(emptyFields)

  const dispatch = useDispatch()
  const { fullOnboardingSections, setFullOnboardingSections } = useOnboarding()

  useEffect(() => {
    if (fullOnboardingSections) {
      const learnOnboarding = fullOnboardingSections.find(
        (section) => section.onboardingSectionID === 'learnCampaignLinks',
      )
      if (
        learnOnboarding &&
        !learnOnboarding.sectionCompleted &&
        toggleBoxState.some((state) => state)
      ) {
        dispatch(
          logActionAsync(onboardingSectionsData.learnCampaignLinks.actionData),
        )
      }
    }
  }, [fullOnboardingSections])

  const refs = {}
  const setRef = (index, ref): void => {
    refs[index] = ref
  }

  const updateState = (index, value, toggleOpen = true) => {
    const copy = toggleBoxState.concat()
    copy[index] = value
    setToggleBoxState(copy)
    saveUserData({ toggleBoxState: copy })

    if (toggleOpen) {
      const copyOpenBoxState = openBoxState.map((_, i) => {
        // Open the next section
        if (i === index + 1) return true
        return false
      })

      if (refs[index]) {
        refs[index].scrollIntoView(true)
      }

      setOpenBoxState(copyOpenBoxState)
    }
  }

  const onToggle = (index, value) => {
    const copy = openBoxState.concat()
    copy[index] = value
    setOpenBoxState(copy)

    // Update onboarding section
    if (Array.isArray(fullOnboardingSections)) {
      const onboardingSectionsCopy = [...fullOnboardingSections]
      const learnLinksSection = onboardingSectionsCopy.findIndex(
        (section) => section.onboardingSectionID === 'learnCampaignLinks',
      )
      if (
        !onboardingSectionsCopy[learnLinksSection].sectionCompleted &&
        value
      ) {
        onboardingSectionsCopy[learnLinksSection].sectionCompleted = true
        setFullOnboardingSections(onboardingSectionsCopy)
        dispatch(
          logActionAsync(onboardingSectionsData.learnCampaignLinks.actionData),
        )
      }
    }
  }

  const sections = adobe ? adobeContent : googleContent

  return (
    <div className={styles.container} key={shortid.generate()}>
      {sections.map((section, index) => {
        return (
          <ToggleBox
            className={styles.toggleBox}
            fwdRef={(self) => {
              setRef(index, self)
            }}
            key={section.heading}
            heading={section.heading}
            checked={toggleBoxState[index]}
            checkedLeft
            open={openBoxState[index]}
            onToggle={(state: boolean) => {
              onToggle(index, state)
            }}
            setChecked={() => updateState(index, !toggleBoxState[index], false)}
          >
            <div className={styles.toggleInner}>
              {section.content}
              <div style={{ marginTop: 30 }} />
              <Button
                onClick={(): void => {
                  updateState(index, true)
                }}
              >
                {index === sections.length - 1 ? 'Got it' : 'Next topic'}
              </Button>
            </div>
          </ToggleBox>
        )
      })}
      <RequestAnExpert
        active={raeActive}
        toggleActive={(): void => setRaeActive(!raeActive)}
      />
    </div>
  )
}
